import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Email marketing service
export const emailMarketingService = {
  // Create email campaign
  async createCampaign(campaignData) {
    try {
      const campaign = {
        ...campaignData,
        createdAt: serverTimestamp(),
        status: 'draft', // draft, scheduled, sent, paused
        sentCount: 0,
        openCount: 0,
        clickCount: 0,
        unsubscribeCount: 0
      };
      
      const docRef = await addDoc(collection(db, 'emailCampaigns'), campaign);
      return { id: docRef.id, ...campaign };
    } catch (error) {
      console.error('Error creating email campaign:', error);
      throw error;
    }
  },

  // Get all campaigns
  async getCampaigns() {
    try {
      const q = query(
        collection(db, 'emailCampaigns'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  },

  // Update campaign
  async updateCampaign(campaignId, updates) {
    try {
      const campaignRef = doc(db, 'emailCampaigns', campaignId);
      await updateDoc(campaignRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  },

  // Send campaign
  async sendCampaign(campaignId) {
    try {
      // This would integrate with email service provider (SendGrid, Mailchimp, etc.)
      // For now, we'll simulate the sending process
      const campaignRef = doc(db, 'emailCampaigns', campaignId);
      await updateDoc(campaignRef, {
        status: 'sent',
        sentAt: serverTimestamp(),
        sentCount: 150 // Simulated count
      });
      
      return { success: true, sentCount: 150 };
    } catch (error) {
      console.error('Error sending campaign:', error);
      throw error;
    }
  }
};

// Email template service
export const emailTemplateService = {
  // Get email templates
  async getTemplates() {
    try {
      const q = query(
        collection(db, 'emailTemplates'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  },

  // Create email template
  async createTemplate(templateData) {
    try {
      const template = {
        ...templateData,
        createdAt: serverTimestamp(),
        isActive: true
      };
      
      const docRef = await addDoc(collection(db, 'emailTemplates'), template);
      return { id: docRef.id, ...template };
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }
};

// Customer segmentation service
export const customerSegmentationService = {
  // Get customer segments
  async getSegments() {
    try {
      const q = query(
        collection(db, 'customerSegments'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching segments:', error);
      throw error;
    }
  },

  // Create customer segment
  async createSegment(segmentData) {
    try {
      const segment = {
        ...segmentData,
        createdAt: serverTimestamp(),
        customerCount: 0
      };
      
      const docRef = await addDoc(collection(db, 'customerSegments'), segment);
      return { id: docRef.id, ...segment };
    } catch (error) {
      console.error('Error creating segment:', error);
      throw error;
    }
  },

  // Get customers in segment
  async getSegmentCustomers(segmentId) {
    try {
      const q = query(
        collection(db, 'users'),
        where('segments', 'array-contains', segmentId)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching segment customers:', error);
      throw error;
    }
  }
};

// Email automation service
export const emailAutomationService = {
  // Create automation rule
  async createAutomationRule(ruleData) {
    try {
      const rule = {
        ...ruleData,
        createdAt: serverTimestamp(),
        isActive: true,
        triggerCount: 0
      };
      
      const docRef = await addDoc(collection(db, 'emailAutomations'), rule);
      return { id: docRef.id, ...rule };
    } catch (error) {
      console.error('Error creating automation rule:', error);
      throw error;
    }
  },

  // Get automation rules
  async getAutomationRules() {
    try {
      const q = query(
        collection(db, 'emailAutomations'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching automation rules:', error);
      throw error;
    }
  },

  // Trigger automation
  async triggerAutomation(ruleId, customerData) {
    try {
      // This would integrate with email service provider
      // For now, we'll simulate the trigger
      const ruleRef = doc(db, 'emailAutomations', ruleId);
      await updateDoc(ruleRef, {
        triggerCount: (await this.getTriggerCount(ruleId)) + 1,
        lastTriggered: serverTimestamp()
      });
      
      return { success: true, triggered: true };
    } catch (error) {
      console.error('Error triggering automation:', error);
      throw error;
    }
  },

  async getTriggerCount(ruleId) {
    try {
      const ruleDoc = await getDocs(doc(db, 'emailAutomations', ruleId));
      return ruleDoc.data()?.triggerCount || 0;
    } catch (error) {
      return 0;
    }
  }
};

// Email analytics service
export const emailAnalyticsService = {
  // Get email analytics
  async getEmailAnalytics() {
    try {
      const campaignsQuery = query(collection(db, 'emailCampaigns'));
      const campaignsSnapshot = await getDocs(campaignsQuery);
      
      const campaigns = campaignsSnapshot.docs.map(doc => doc.data());
      
      const analytics = {
        totalCampaigns: campaigns.length,
        totalSent: campaigns.reduce((sum, campaign) => sum + (campaign.sentCount || 0), 0),
        totalOpens: campaigns.reduce((sum, campaign) => sum + (campaign.openCount || 0), 0),
        totalClicks: campaigns.reduce((sum, campaign) => sum + (campaign.clickCount || 0), 0),
        averageOpenRate: 0,
        averageClickRate: 0,
        recentCampaigns: campaigns
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5)
      };
      
      // Calculate rates
      if (analytics.totalSent > 0) {
        analytics.averageOpenRate = (analytics.totalOpens / analytics.totalSent) * 100;
        analytics.averageClickRate = (analytics.totalClicks / analytics.totalSent) * 100;
      }
      
      return analytics;
    } catch (error) {
      console.error('Error fetching email analytics:', error);
      throw error;
    }
  }
};
